import React, { useState } from 'react';
import { useTranslation, useCountryInView, useLanguageMenu } from '../../i18n/useTranslation';
import { useTheme } from '../../themes/ThemeProvider';
import { tokens, getContextColor } from '../../styles/designTokens';
import { getCountryFlag } from '../../utils/countryHelpers';

/**
 * LanguageSelector Component
 *
 * The country flag (the country in view) stacked above the language menu.
 * Stacked layout: country flag on top, the current language's short code below; click the code
 * to open the menu.
 *
 * The menu (Roadmap #85, docs/briefs/2026-10-01-country-default-language-addendum-2.md, R5′)
 * lists every offered language, by its own name, in the platform's order — the same everywhere,
 * read from platform_languages, never a list in code. It is shown only when more than one language
 * is offered; the flag shows either way (the mobile front-page header relies on it).
 *
 * Position: Left of My Profile button in headers (centered 40px left)
 * Storage: a member's choice on their account, a visitor's in this browser (TranslationProvider).
 *
 * @param {string} countryCode - Country whose flag to show. Defaults to the country in view;
 *   MyLocations passes its own picker's country.
 */
const LanguageSelector = ({ countryCode }) => {
  const { t } = useTranslation('common');
  const { t: tLoc } = useTranslation('location');
  const { languages, locale, changeLocale } = useLanguageMenu();
  const countryInView = useCountryInView();
  const theme = useTheme();
  const [showDropdown, setShowDropdown] = useState(false);

  const flagCountry = countryCode || countryInView;
  const nameKey = `country_name_${flagCountry.toLowerCase()}`;
  const countryName = tLoc(nameKey);
  const flagTitle = countryName === `location.${nameKey}` ? flagCountry : countryName;

  const showMenu = languages.length > 1;
  const activeColor = getContextColor(theme);

  const handleLanguageSelect = (newLocale) => {
    changeLocale(newLocale);
    setShowDropdown(false);
  };

  // Map locale to display code (pt-PT → PT, en → EN)
  const displayCode = locale.split('-')[0].toUpperCase();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '2px',
        position: 'relative'
      }}
    >
      {/* Country flag emoji - shows the country in view. Reduced ~20% (Mobile Groups, Commit 5)
          so the shared switcher reads as a quiet element in both the locality and group headers. */}
      <span
        style={{
          fontSize: '16px',
          lineHeight: 1,
          userSelect: 'none'
        }}
        title={flagTitle}
      >
        {getCountryFlag(flagCountry)}
      </span>

      {/* Language text - clickable (reduced ~20% with the flag) */}
      {showMenu && (
        <div
          onClick={() => setShowDropdown(!showDropdown)}
          style={{
            fontSize: '11px',
            fontWeight: '600',
            color: '#333',
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'opacity 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.opacity = '0.7'}
          onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
          title={t('language_menu_title')}
        >
          {displayCode}
        </div>
      )}

      {/* Dropdown menu */}
      {showMenu && showDropdown && (
        <>
          {/* Dropdown overlay to close on outside click */}
          <div
            onClick={() => setShowDropdown(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: tokens.zIndex.dropdown
            }}
          />

          {/* Dropdown content — one row per offered language, by its own name (never translated) */}
          <div
            style={{
              position: 'absolute',
              top: '38px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
              overflow: 'hidden',
              zIndex: tokens.zIndex.dropdown,
              minWidth: '90px'
            }}
          >
            {languages.map(({ locale: optionLocale, native_name: nativeName }) => {
              const isActive = optionLocale === locale;
              return (
                <div
                  key={optionLocale}
                  lang={optionLocale}
                  onClick={() => handleLanguageSelect(optionLocale)}
                  style={{
                    padding: '6px 10px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: isActive ? activeColor : '#333',
                    backgroundColor: isActive ? tokens.colors.headerBackground : 'white',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => {
                    if (!isActive) e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseOut={(e) => {
                    if (!isActive) e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  {nativeName}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSelector;
